import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
  Box,
  Typography,
  FormControlLabel,
  Switch,
  Checkbox,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Tooltip } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileWord,
  faFileImage,
  faFilePdf,
  faFileExcel,
  faFile,
} from "@fortawesome/free-solid-svg-icons";
import { useSnackbar } from "../../contexts/snackbar-context";
import { useApiWithHandling } from "../../hooks/useApiWithHandling";
import { useHasPermission } from "../../hooks/useHasPermission";
import Breadcrumb, { BreadcrumbItem } from "../common/breadcrumb";
import ConfirmDialog from "../common/confirm-dialog";
import { useDocumentService, FolderExportJobResponse } from "../../services/documentService";
import { useSubAssemblyService } from "../../services/subAssemblyService";
import { triggerExportJobDownloads, getExportDownloadStartMessage } from "../../utils/folderExportDownloads";
import { formatDocumentUploadInfo } from "../../utils/documentUploadInfo";
import {
  buildAirframeRootExportLabel,
  buildAssemblyRootExportLabel,
  buildFolderPathExportLabel,
  FOLDER_EXPORT_IN_PROGRESS_TOOLTIP,
  findActiveExportJobByLabel,
  isFolderExportJobActive,
  jobMatchesExportLabel,
} from "../../utils/folderExportJobUtils";
import { useFolderExportSignalR } from "../../hooks/useFolderExportSignalR";
import { useDocumentMoveJobUpdates } from "../../hooks/useDocumentMoveJobUpdates";
import FailedDocumentModal from "./failed-documentmodal";
import CompareDirectoryDialog from "./compare-directory-dialog";
import MoveFolderPickerDrawer from "./MoveFolderPickerDrawer";
import FolderConflictDialog from "./FolderConflictDialog";
import {
  BRAND_ORANGE,
  BRAND_ORANGE_HOVER,
  BRAND_ORANGE_SELECTED_BG,
  BRAND_ORANGE_SELECTED_HOVER,
} from "../../constants/constants";
import {
  appendQuickUploadHistory,
  clearQuickUploadHistory,
  readQuickUploadHistory,
  type QuickUploadHistoryEntry,
} from "../../utils/documentQuickUploadHistory";
import { isArchiveDocument } from "../../utils/archiveDocumentUtils";
import { isHtmlDocument } from "../../utils/htmlDocumentUtils";
import { hasSiblingFolderName } from "../../utils/folderNameUtils";

const collectDocumentIdsInFolderSubtrees = (
  folderIds: Iterable<string>,
  rootFolders: Folder[]
): Set<string> => {
  const targetIds = new Set(folderIds);
  const docIds = new Set<string>();

  const collectSubtree = (folder: Folder) => {
    folder.documents.forEach((doc) => docIds.add(doc.id));
    folder.children.forEach(collectSubtree);
  };

  const walk = (list: Folder[]) => {
    for (const folder of list) {
      if (targetIds.has(folder.id)) {
        collectSubtree(folder);
      } else if (folder.children.length > 0) {
        walk(folder.children);
      }
    }
  };

  walk(rootFolders);
  return docIds;
};

const buildMoveLockSets = (
  selectedDocIds: Set<string>,
  selectedFolderIds: Set<string>,
  rootFolders: Folder[]
) => {
  const folderIds = new Set(selectedFolderIds);
  const docIds = new Set(selectedDocIds);
  for (const docId of Array.from(
    collectDocumentIdsInFolderSubtrees(selectedFolderIds, rootFolders)
  )) {
    docIds.add(docId);
  }
  return { docIds, folderIds };
};

const pathsEqual = (left: string[], right: string[]): boolean =>
  left.length === right.length
  && left.every((segment, index) => segment === right[index]);

const findFolderByPathInTree = (
  path: string[],
  rootFolders: Folder[]
): Folder | null => {
  if (path.length === 0) {
    return null;
  }

  let currentFolders = rootFolders;
  let currentFolder: Folder | null = null;
  for (const name of path) {
    currentFolder = currentFolders.find((folder) => folder.name === name) || null;
    if (!currentFolder) {
      return null;
    }
    currentFolders = currentFolder.children;
  }

  return currentFolder;
};

const collectVisibleIdsAtPath = (
  path: string[],
  rootFolders: Folder[],
  assemblyRootDocuments: Document[]
): { folderIds: Set<string>; docIds: Set<string> } => {
  const folderIds = new Set<string>();
  const docIds = new Set<string>();

  if (path.length === 0) {
    rootFolders.forEach((folder) => folderIds.add(folder.id));
    assemblyRootDocuments.forEach((doc) => docIds.add(doc.id));
    return { folderIds, docIds };
  }

  const currentFolder = findFolderByPathInTree(path, rootFolders);
  if (!currentFolder) {
    return { folderIds, docIds };
  }

  currentFolder.children.forEach((folder) => folderIds.add(folder.id));
  currentFolder.documents.forEach((doc) => docIds.add(doc.id));
  return { folderIds, docIds };
};

const MOVE_ROW_RELEASE_POLL_MS = 2000;
const MOVE_ROW_RELEASE_MAX_ATTEMPTS = 45;

export type DocumentListRefreshResult = {
  folders: Folder[];
  assemblyRootDocuments: Document[];
};

export interface Document {
  id: string;
  title: string;
  description: string;
  status: string;
  securityLevel: string;
  categoryCode: string;
  categoryName: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  tailNumber: string;
  aircraftType: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
  folderPath?: string;
}

export interface Folder {
  id: string;
  name: string;
  code: string;
  path: string;
  description: string;
  parentId: string | null;
  children: Folder[];
  documents: Document[];
  totalDocumentCount: number;
  level: number;
  hasChildren: boolean;
  hasDocuments: boolean;
  assemblyType?: string;
  componentId?: string;
  subAssemblyId?: string;
}

export interface NavigationState {
  currentPath: string[];
  currentFolder: Folder | null;
  showingFiles: boolean;
}

interface DocumentListProps {
  folders: Folder[];
  assemblyRootDocuments?: Document[];
  navigationState: NavigationState;
  onToggleFolder: (index: number) => void;
  onNavigateToFolder: (folderName: string) => void;
  onNavigateBack: (path: string[], options?: { replace?: boolean }) => void;
  onViewDocument: (doc: Document) => void;
  onDownloadDocument: (doc: Document) => void;
  onDeleteDocument: (doc: Document) => void;
  lastSearchRequest?: any;
  overrideTailNumber?: string;
  airframeId?: string;
  onRefresh?: () => Promise<DocumentListRefreshResult | null | void>;
  isGroupedMode?: boolean;
  /** Bumped by parent on each document search so history reloads from storage after clear. */
  quickUploadHistoryEpoch?: number;
  allFolders?: Folder[];
}

const dialogPaperSx = {
  backgroundColor: "#111",
  color: "#fff",
  border: "1px solid #333",
};

const dialogTitleSx = {
  color: "hsl(19 101% 51% / 1)",
};

const dialogTextFieldSx = {
  mt: 1,
  "& .MuiInputLabel-root": {
    color: "#ccc",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "hsl(19 101% 51% / 1)",
  },
  "& .MuiOutlinedInput-root": {
    color: "#fff",
    "& fieldset": {
      borderColor: "#666",
    },
    "&:hover fieldset": {
      borderColor: "#999",
    },
    "&.Mui-focused fieldset": {
      borderColor: "hsl(19 101% 51% / 1)",
    },
  },
};

const dialogPrimaryButtonSx = {
  backgroundColor: "hsl(19 101% 51% / 1)",
  "&:hover": {
    backgroundColor: "hsl(19 101% 41% / 1)",
  },
  "&:disabled": {
    backgroundColor: "hsl(19 101% 51% / 0.4)",
    color: "#fff",
  },
};

const DocumentList: React.FC<DocumentListProps> = ({
  folders,
  assemblyRootDocuments = [],
  navigationState,
  onToggleFolder,
  onNavigateToFolder,
  onNavigateBack,
  onViewDocument,
  onDownloadDocument,
  onDeleteDocument,
  lastSearchRequest,
  overrideTailNumber,
  airframeId,
  onRefresh,
  isGroupedMode = false,
  quickUploadHistoryEpoch = 0,
  allFolders,
}) => {
  const { showSuccess, showError, showInfo } = useSnackbar();
  const navigate = useNavigate();
  const { getBlob, get, post } = useApiWithHandling();
  const {
    uploadDocument,
    createCustomFolder,
    renameCustomFolder,
    deleteCustomFolder,
    startFolderExportJob,
    getFolderExportJob,
    listFolderExportJobs,
  } = useDocumentService();
  const { getSubAssemblies } = useSubAssemblyService();
  const canDeleteDocument = useHasPermission('documents-upload', 'delete');
  const canViewDocument = useHasPermission('documents-search', 'read');
  const canEditDocument = useHasPermission('documents-upload', 'update');
  const canCreateDocument = useHasPermission('documents-upload', 'create');
  const canMoveDocuments = useHasPermission('documents-move', 'create');
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(
    new Set()
  );
  const [isDragging, setIsDragging] = useState(false);
  const [isDropUploading, setIsDropUploading] = useState(false);
  const [sourceFolders, setSourceFolders] = useState<Folder[]>(folders);
  const [localFolders, setLocalFolders] = useState<Folder[]>(folders);
  const [activeDropFolder, setActiveDropFolder] = useState<Folder | null>(null);
  const [hideEmptyFolders, setHideEmptyFolders] = useState(true);
  const [isCreateFolderDialogOpen, setIsCreateFolderDialogOpen] = useState(false);
  const [folderPendingCreate, setFolderPendingCreate] = useState<Folder | null>(null);
  const [pendingFolderName, setPendingFolderName] = useState("");
  const [isRenameFolderDialogOpen, setIsRenameFolderDialogOpen] = useState(false);
  const [folderPendingRename, setFolderPendingRename] = useState<Folder | null>(null);
  const [folderPendingDelete, setFolderPendingDelete] = useState<Folder | null>(null);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [isRenamingFolder, setIsRenamingFolder] = useState(false);
  const [isDeletingFolder, setIsDeletingFolder] = useState(false);
  const [isDownloadingRootArchive, setIsDownloadingRootArchive] = useState(false);
  const [downloadingFolders, setDownloadingFolders] = useState<Set<string>>(
    new Set()
  );
  const [activeExportJobs, setActiveExportJobs] = useState<FolderExportJobResponse[]>([]);
  const listExportJobsRef = useRef(listFolderExportJobs);
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [uploadHistoryOpen, setUploadHistoryOpen] = useState(false);
  const [uploadHistoryEntries, setUploadHistoryEntries] = useState<
    QuickUploadHistoryEntry[]
  >(() => readQuickUploadHistory());
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());
  const [selectedFolderIds, setSelectedFolderIds] = useState<Set<string>>(new Set());
  const [isMoveMode, setIsMoveMode] = useState(false);
  const [isMoveDrawerOpen, setIsMoveDrawerOpen] = useState(false);
  const [isConflictDialogOpen, setIsConflictDialogOpen] = useState(false);
  const [pendingMoveParams, setPendingMoveParams] = useState<{
    targetFolderId: string;
    targetFolderName: string;
    targetFolderPath: string;
  } | null>(null);
  const lastCheckedIndex = useRef<number>(-1);
  const [lockedDocIds, setLockedDocIds] = useState<Set<string>>(new Set());
  const [lockedFolderIds, setLockedFolderIds] = useState<Set<string>>(new Set());
  const [moveSourcePath, setMoveSourcePath] = useState<string[] | null>(null);
  const pendingMoveDocIdsRef = useRef<Set<string>>(new Set());
  const pendingMoveFolderIdsRef = useRef<Set<string>>(new Set());
  const moveRowReleaseRunIdRef = useRef(0);
  const [activeMoveJob, setActiveMoveJob] = useState<{
    jobId: string;
    totalFileCount: number;
    completedFileCount: number;
    failedFileCount: number;
    status: string;
    targetFolderName: string;
  } | null>(null);

  const isTerminalMoveStatus = (status: string): boolean =>
    status === "Completed" || status === "CompletedWithErrors" || status === "Failed";

  const clearMoveRowLocks = useCallback(() => {
    pendingMoveDocIdsRef.current = new Set();
    pendingMoveFolderIdsRef.current = new Set();
    setLockedDocIds(new Set());
    setLockedFolderIds(new Set());
    setMoveSourcePath(null);
  }, []);

  const syncMoveRowsAfterJob = useCallback(
    async (jobFailed: boolean) => {
      const runId = moveRowReleaseRunIdRef.current + 1;
      moveRowReleaseRunIdRef.current = runId;
      const sourcePath = moveSourcePath;
      const onSourcePage =
        sourcePath !== null
        && pathsEqual(navigationState.currentPath, sourcePath);

      if (!onSourcePage) {
        if (moveRowReleaseRunIdRef.current === runId) {
          clearMoveRowLocks();
        }
        if (onRefresh) {
          await onRefresh();
        }
        return;
      }

      if (jobFailed) {
        if (moveRowReleaseRunIdRef.current === runId) {
          clearMoveRowLocks();
        }
        if (onRefresh) {
          await onRefresh();
        }
        return;
      }

      for (let attempt = 0; attempt < MOVE_ROW_RELEASE_MAX_ATTEMPTS; attempt += 1) {
        if (moveRowReleaseRunIdRef.current !== runId) {
          return;
        }

        const refreshed = onRefresh ? await onRefresh() : null;
        const treeFolders = refreshed?.folders ?? allFolders ?? localFolders;
        const treeAssemblyRootDocuments =
          refreshed?.assemblyRootDocuments ?? assemblyRootDocuments;
        const { folderIds, docIds } = collectVisibleIdsAtPath(
          sourcePath ?? [],
          treeFolders,
          treeAssemblyRootDocuments
        );

        const remainingFolderIds = new Set<string>();
        for (const folderId of Array.from(pendingMoveFolderIdsRef.current)) {
          if (folderIds.has(folderId)) {
            remainingFolderIds.add(folderId);
          }
        }

        const remainingDocIds = new Set<string>();
        for (const docId of Array.from(pendingMoveDocIdsRef.current)) {
          if (docIds.has(docId)) {
            remainingDocIds.add(docId);
          }
        }

        pendingMoveFolderIdsRef.current = remainingFolderIds;
        pendingMoveDocIdsRef.current = remainingDocIds;
        setLockedFolderIds(remainingFolderIds);
        setLockedDocIds(remainingDocIds);

        if (remainingFolderIds.size === 0 && remainingDocIds.size === 0) {
          setMoveSourcePath(null);
          return;
        }

        if (attempt < MOVE_ROW_RELEASE_MAX_ATTEMPTS - 1) {
          await new Promise<void>((resolve) => {
            window.setTimeout(resolve, MOVE_ROW_RELEASE_POLL_MS);
          });
        }
      }

      if (moveRowReleaseRunIdRef.current === runId) {
        clearMoveRowLocks();
      }
    },
    [
      allFolders,
      assemblyRootDocuments,
      clearMoveRowLocks,
      localFolders,
      moveSourcePath,
      navigationState.currentPath,
      onRefresh,
    ]
  );

  useEffect(() => {
    return () => {
      moveRowReleaseRunIdRef.current += 1;
    };
  }, []);

  useEffect(() => {
    setUploadHistoryEntries(readQuickUploadHistory());
  }, [quickUploadHistoryEpoch]);

  useEffect(() => {
    if (isMoveDrawerOpen) {
      return;
    }

    setSelectedDocIds(new Set());
    setSelectedFolderIds(new Set());
    setIsMoveMode(false);
    setIsMoveDrawerOpen(false);
    lastCheckedIndex.current = -1;
  }, [navigationState.currentPath, navigationState.showingFiles, isMoveDrawerOpen]);

  const getAllDocumentIds = (folders: Folder[]): Set<string> => {
    const ids = new Set<string>();
    const traverse = (folderList: Folder[]) => {
      folderList.forEach((folder) => {
        folder.documents.forEach((doc) => ids.add(doc.id));
        if (folder.children?.length > 0) traverse(folder.children);
      });
    };
    traverse(folders);
    return ids;
  };

  useEffect(() => {
    if (selectedDocIds.size === 0) return;
    const availableIds = getAllDocumentIds(localFolders);
    // Loose files at the assembly/warehouse root are not nested under folder.documents.
    // After detach they still render via assemblyRootDocuments; omit them here and the
    // checkbox checks then immediately unchecks (prune thinks the file vanished).
    assemblyRootDocuments.forEach((doc) => availableIds.add(doc.id));
    const pruned = new Set(
      Array.from(selectedDocIds).filter((id) => availableIds.has(id))
    );
    if (pruned.size !== selectedDocIds.size) {
      setSelectedDocIds(pruned);
    }
  }, [assemblyRootDocuments, localFolders, selectedDocIds]);

  const clearDocumentSelection = useCallback(() => {
    setSelectedDocIds(new Set());
    setSelectedFolderIds(new Set());
    lastCheckedIndex.current = -1;
  }, []);

  const openMoveDrawer = useCallback(() => {
    setIsMoveDrawerOpen(true);
  }, []);

  const handleMoveConfirm = async (
    targetFolderId: string,
    targetFolderName: string,
    targetFolderPath: string,
    conflictResolution: string = "None"
  ) => {
    const docIds = Array.from(selectedDocIds);
    const folderIds = Array.from(selectedFolderIds);

    if (docIds.length === 0 && folderIds.length === 0) {
      return;
    }

    const isAssembly =
      lastSearchRequest?.Filters?.documentScope === "assembly" ||
      lastSearchRequest?.Filters?.documentScope === "subAssembly";

    const assemblyType = isAssembly
      ? (lastSearchRequest?.Filters?.assemblyType
        ?? lastSearchRequest?.Filters?.subAssemblyType)
      : undefined;
    const assemblyFolder = isAssembly
      ? (lastSearchRequest?.Filters?.assemblyFolder
        ?? lastSearchRequest?.Filters?.subAssemblyFolder)
      : undefined;
    const componentId = isAssembly
      ? lastSearchRequest?.Filters?.componentId
      : undefined;

    try {
      setIsMoveDrawerOpen(false);

      let resolvedAirframeId: string | undefined = airframeId?.trim() || undefined;
      const tailNumber = isAssembly
        ? undefined
        : (lastSearchRequest?.TailNumber ?? overrideTailNumber ?? resolveFolderTailNumber());

      if (!isAssembly && !resolvedAirframeId && tailNumber) {
        const airframesResponse = await get<any>("api/airframes", {
          showLoader: false,
          showErrorSnackbar: false,
          showSuccessSnackbar: false,
        });
        const airframeList =
          airframesResponse?.data?.data ??
          airframesResponse?.data ??
          [];
        const matchedAirframe = (Array.isArray(airframeList) ? airframeList : []).find(
          (item: { id?: string; Id?: string; tailNumber?: string; TailNumber?: string }) => {
            const itemTail = item.tailNumber ?? item.TailNumber ?? "";
            return itemTail.localeCompare(tailNumber, undefined, { sensitivity: "accent" }) === 0;
          }
        );
        resolvedAirframeId = matchedAirframe?.id ?? matchedAirframe?.Id ?? undefined;
      }

      const response = await post<{
        jobId: string;
        status: string;
        totalFileCount: number;
        targetFolderName: string;
      }>(
        "api/documents/move",
        {
          documentIds: docIds,
          folderIds: folderIds,
          targetFolderId,
          targetFolderName,
          targetFolderPath,
          sourceFolderName: navigationState.currentFolder?.name ?? undefined,
          airframeId: isAssembly ? undefined : resolvedAirframeId,
          tailNumber: isAssembly ? undefined : tailNumber,
          assemblyType,
          assemblyFolder,
          componentId,
          conflictResolution,
        },
        {
          showLoader: false,
          showErrorSnackbar: false,
          showSuccessSnackbar: false,
          throwOnError: true,
        }
      );

      const moveResult = response?.data?.jobId
        ? response.data
        : (response?.data as { data?: { jobId: string; totalFileCount: number; targetFolderName: string } })?.data;

      if (moveResult?.jobId) {
        const rootFolders = allFolders ?? localFolders;
        const { docIds, folderIds: lockedFolders } = buildMoveLockSets(
          selectedDocIds,
          selectedFolderIds,
          rootFolders
        );
        pendingMoveDocIdsRef.current = new Set(docIds);
        pendingMoveFolderIdsRef.current = new Set(lockedFolders);
        setMoveSourcePath([...navigationState.currentPath]);
        setLockedDocIds(docIds);
        setLockedFolderIds(lockedFolders);

        setActiveMoveJob({
          jobId: moveResult.jobId,
          totalFileCount: moveResult.totalFileCount,
          completedFileCount: 0,
          failedFileCount: 0,
          status: "InProgress",
          targetFolderName: moveResult.targetFolderName ?? targetFolderName,
        });
        clearDocumentSelection();
        showSuccess(
          `Move started — ${moveResult.totalFileCount} ` +
          `item(s) moving to '${targetFolderName}'`
        );
      }
    } catch (error: unknown) {
      const apiError = error as { status?: number; response?: { status?: number }; message?: string };
      if (
        apiError?.status === 409 ||
        apiError?.response?.status === 409 ||
        apiError?.message?.includes("already exists")
      ) {
        setPendingMoveParams({
          targetFolderId,
          targetFolderName,
          targetFolderPath,
        });
        setIsConflictDialogOpen(true);
        setIsMoveDrawerOpen(true);
      } else {
        showError("Failed to start move job. Please try again.");
      }
    }
  };

  const handleConflictResolution = async (resolution: string) => {
    setIsConflictDialogOpen(false);
    if (!pendingMoveParams) return;

    await handleMoveConfirm(
      pendingMoveParams.targetFolderId,
      pendingMoveParams.targetFolderName,
      pendingMoveParams.targetFolderPath,
      resolution
    );
    setPendingMoveParams(null);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }

      if (isMoveDrawerOpen) {
        setIsMoveDrawerOpen(false);
        return;
      }

      if (isMoveMode) {
        clearDocumentSelection();
        setIsMoveMode(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [clearDocumentSelection, isMoveDrawerOpen, isMoveMode]);

  useDocumentMoveJobUpdates(
    activeMoveJob?.jobId ?? null,
    (data) => {
      setActiveMoveJob((prev) =>
        prev
          ? {
              ...prev,
              completedFileCount: data.completedFileCount,
              failedFileCount: data.failedFileCount,
              status: data.status,
            }
          : null
      );

      if (data.type === "DocumentMoveComplete") {
        if (data.status === "Completed") {
          showSuccess(
            `Move complete — ${data.completedFileCount} ` +
            `file(s) moved to '${data.targetFolderName}'. View Move Jobs for details.`
          );
        } else if (data.status === "CompletedWithErrors") {
          showError(
            `Move finished with errors — ` +
            `${data.completedFileCount} moved, ` +
            `${data.failedFileCount} failed. View Move Jobs for details.`
          );
        } else {
          showError("Move job failed. No files were moved. View Move Jobs for details.");
        }

        void syncMoveRowsAfterJob(data.status === "Failed");
      }
    }
  );

  useEffect(() => {
    if (!activeMoveJob || !isTerminalMoveStatus(activeMoveJob.status)) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setActiveMoveJob((previous) =>
        previous?.jobId === activeMoveJob.jobId ? null : previous
      );
    }, 5000);

    return () => window.clearTimeout(timeoutId);
  }, [activeMoveJob]);

  const [showFixModal, setShowFixModal] = useState(false);
  const [selectedFailedDoc, setSelectedFailedDoc] = useState<Document | null>(
    null
  );
   
  const isLandingGearSubAssemblyFolder = (folder: Folder): boolean => {
    if (!folder.code || folder.code === "CUSTOM") {
      return false;
    }

    if (/^[FGI]$/i.test(folder.code) || /^I\d{3}$/i.test(folder.code)) {
      return false;
    }

    const hasLandingGearRecordChildren = folder.children?.some((child) =>
      /^I\d{3}$/i.test(child.code)
    );

    return Boolean(
      hasLandingGearRecordChildren &&
      folder.code.includes(".") &&
      /SN/i.test(folder.code)
    );
  };

  const resolveDocumentTypeCode = (folder: Folder): string => {
    // DocumentTypeCode is Always an IATA leaf category (F001, A003, I005, etc.)
    if (folder.documents && folder.documents.length > 0) {
      return folder.documents[0].categoryCode;
    }

    if (isLandingGearSubAssemblyFolder(folder)) {
      return "I";
    }

    // Fallback to folder.code which should be the IATA category code
    return folder.code;
  };

  const resolveSelectedFolderCode = (folder: Folder, isAssemblyScope: boolean): string => {
    if (!isAssemblyScope) {
      return folder.path;
    }

    if (folder.code === "CUSTOM") {
      return folder.path;
    }

    if (isLandingGearSubAssemblyFolder(folder)) {
      return "I";
    }

    return folder.code;
  };

  /**
   * OffWing/pool SA views project category paths as "{Type.Serial}/{I00x}".
   * assemblyFolder is already that SA root, so strip the leading duplicate segment
   * before sending assemblyCategory (avoids .../SideStrut.X/SideStrut.X/I001).
   */
  const resolveAssemblyCategory = (
    folder: Folder,
    assemblyFolderName: string
  ): string | undefined => {
    const path = (folder.path ?? "").replace(/\\/g, "/").replace(/^\/+|\/+$/g, "");
    if (!path) {
      return undefined;
    }

    const saFolder = assemblyFolderName.trim();
    const looksLikeDetachedSa =
      saFolder.includes(".") &&
      /SN/i.test(saFolder) &&
      saFolder.indexOf(".") === saFolder.lastIndexOf(".");

    if (looksLikeDetachedSa) {
      if (path.localeCompare(saFolder, undefined, { sensitivity: "accent" }) === 0) {
        return undefined;
      }

      const prefix = `${saFolder}/`;
      if (path.toLowerCase().startsWith(prefix.toLowerCase())) {
        const relative = path.slice(prefix.length);
        return relative || undefined;
      }
    }

    return path;
  };

  const isCustomFolder = (folder: Folder | null | undefined): boolean => {
    if (!folder?.id) {
      return false;
    }

    const guidPattern =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return guidPattern.test(folder.id);
  };

  const isAssemblyScopedSearch = (documentScope?: string): boolean => {
    return documentScope === "assembly" || documentScope === "subAssembly";
  };

  const isSubAssemblyRootFolderCode = (folderCode: string | undefined): boolean => {
    const normalizedCode = (folderCode ?? "").trim().toUpperCase();
    if (!normalizedCode) {
      return false;
    }

    return /^I\d{3}$/.test(normalizedCode) || /^\d{4}$/.test(normalizedCode);
  };

  //sync folders prop to local state
  React.useEffect(() => {
    setSourceFolders(folders);
  }, [folders]);

  React.useEffect(() => {
    setLocalFolders(sourceFolders);
  }, [sourceFolders]);

  //refetch the list after upload
  const refreshFolderData = async () => {
    if (onRefresh) {
      try {
        await onRefresh();
      } catch {
        showError("Failed to refresh documents");
      }
      return;
    }

    // use the default refresh logic
    if (!lastSearchRequest) return;

    try {
      const response = await post("api/documents/search", lastSearchRequest, {
        showLoader: false,
        showErrorSnackbar: true,
        showSuccessSnackbar: false,
      });

      if (response?.data?.data?.folders) {
        updateSourceFolders(response.data.data.folders);
      }
    } catch {
      showError("Failed to refresh documents");
    }
  };

  const updateSourceFolders = (newFolders: Folder[]) => {
    setSourceFolders(newFolders);
  };

  // Function to find folder by path
  const findFolderByPath = (
    path: string[],
    rootFolders: Folder[]
  ): Folder | null => {
    if (path.length === 0) return null;
    let currentFolders = rootFolders;
    let currentFolder: Folder | null = null;
    for (const name of path) {
      currentFolder = currentFolders.find((f) => f.name === name) || null;
      if (!currentFolder) return null;
      currentFolders = currentFolder.children;
    }
    return currentFolder;
  };

  // Filter folders to hide those with 0 documents
  const filterEmptyFolders = (folders: Folder[]): Folder[] => {
    if (!hideEmptyFolders) {
      return folders;
    }
    
    return folders
      .map((folder) => {
        const visibleChildren = filterEmptyFolders(folder.children);

        return {
          ...folder,
          children: visibleChildren,
        };
      })
      .filter((folder) => {
        if (folder.children.length > 0) {
          return true;
        }

        if (folder.code === "CUSTOM") {
          return true;
        }

        return folder.totalDocumentCount > 0;
      });
  };

  // Get current items to display and  modified to include localFiles
  const getCurrentItems = (): (Folder | Document)[] => {
    const foldersToUse = filterEmptyFolders(localFolders);
    
    if (navigationState.currentPath.length === 0) {
      return [...foldersToUse, ...assemblyRootDocuments];
    } else {
      const currentFolder = findFolderByPath(
        navigationState.currentPath,
        foldersToUse
      );

      if (currentFolder) {
        return [...currentFolder.children, ...currentFolder.documents];
      }
      return [];
    }
  };

  const getCurrentDocuments = (): Document[] => {
    return getCurrentItems().filter(
      (item): item is Document => "fileName" in item
    );
  };

  const getCurrentFolders = (): Folder[] => {
    return getCurrentItems().filter(
      (item): item is Folder => !("fileName" in item)
    );
  };

  const currentItems = getCurrentItems();
  const onMoveSourcePage =
    moveSourcePath !== null
    && pathsEqual(navigationState.currentPath, moveSourcePath);
  const isMoveInProgress =
    activeMoveJob !== null && !isTerminalMoveStatus(activeMoveJob.status);
  const hasDocumentsInView = getCurrentItems().some(
    (item) => "fileName" in item
  );
  const hasFoldersInView = getCurrentItems().some(
    (item) => !("fileName" in item) && isCustomFolder(item as Folder)
  );
  const moveModeLabel =
    hasDocumentsInView && hasFoldersInView
      ? "Move files/folders"
      : hasDocumentsInView
        ? "Move files"
        : hasFoldersInView
          ? "Move folders"
          : "Move";

  useEffect(() => {
    if (hasDocumentsInView || hasFoldersInView) {
      return;
    }

    if (isMoveMode || selectedDocIds.size > 0 || selectedFolderIds.size > 0) {
      setIsMoveMode(false);
      clearDocumentSelection();
    }
  }, [
    clearDocumentSelection,
    hasDocumentsInView,
    hasFoldersInView,
    isMoveMode,
    selectedDocIds.size,
    selectedFolderIds.size,
  ]);

  useEffect(() => {
    if (selectedFolderIds.size === 0) return;
    const availableFolderIds = new Set(
      getCurrentFolders()
        .filter((f) => isCustomFolder(f))
        .map((f) => f.id)
    );
    const pruned = new Set(
      Array.from(selectedFolderIds).filter((id) => availableFolderIds.has(id))
    );
    if (pruned.size !== selectedFolderIds.size) {
      setSelectedFolderIds(pruned);
    }
    // getCurrentFolders is derived from localFolders + navigation; intentional tree-driven prune.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localFolders, selectedFolderIds]);
  const activeNavigationFolder = navigationState.currentPath.length > 0
    ? findFolderByPath(navigationState.currentPath, localFolders)
    : null;

  const hasValidFormNumber = (doc: Document): boolean => {
    return Boolean(doc.title && !doc.title.includes('.'));
  };

  const closeCreateFolderDialog = () => {
    setIsCreateFolderDialogOpen(false);
    setFolderPendingCreate(null);
    setPendingFolderName("");
  };

  const closeRenameFolderDialog = () => {
    setIsRenameFolderDialogOpen(false);
    setFolderPendingRename(null);
    setPendingFolderName("");
  };

  const closeDeleteFolderDialog = () => {
    setFolderPendingDelete(null);
  };

  const handleCreateFolderRequest = () => {
    setIsCreateFolderDialogOpen(true);
    setFolderPendingCreate(activeNavigationFolder);
    setPendingFolderName("");
  };

  const isGuid = (value: string): boolean => {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
  };

  const normalizeSubAssemblySerial = (value: string): string => {
    const normalized = value.trim().toUpperCase();
    if (!normalized) {
      return "";
    }

    return normalized.startsWith("SN") ? normalized : `SN${normalized}`;
  };

  const toPascalCase = (value: string): string => {
    return value
      .trim()
      .split(/[\s_-]+/)
      .filter(Boolean)
      .map((word) => word[0].toUpperCase() + word.slice(1).toLowerCase())
      .join("");
  };

  const buildSubAssemblyFolderName = (subAssemblyType: string, serialNumber: string): string => {
    const typeKey = subAssemblyType.trim().toUpperCase();
    const prefixByType: Record<string, string> = {
      "SHOCK STRUT": "ShockStrut",
      "SIDE STRUT": "SideStrut",
      "2ND SIDE STRUT": "SecondSideStrut",
      "DRAG BRACE": "DragBrace",
      "AUX DRAG": "AuxDrag",
    };

    const prefix = prefixByType[typeKey] ?? toPascalCase(subAssemblyType);
    const serial = normalizeSubAssemblySerial(serialNumber);
    return `${prefix}.${serial}`;
  };

  const resolveSubAssemblyIdForCreate = async (
    existingSubAssemblyId: string | undefined,
    assemblyType: string | undefined,
    attachedIataNodeKey: string | undefined,
    isAssemblyFolderCreate: boolean
  ): Promise<string | undefined> => {
    if (existingSubAssemblyId) {
      return existingSubAssemblyId;
    }

    if (!isAssemblyFolderCreate) {
      return undefined;
    }

    const normalizedAssemblyType = (assemblyType ?? "")
      .replace(/[-_\s]/g, "")
      .toLowerCase();

    if (normalizedAssemblyType !== "landinggear") {
      return undefined;
    }

    const candidatePath = attachedIataNodeKey?.trim();
    if (!candidatePath) {
      return undefined;
    }

    const folderToken = candidatePath.split("/")[0]?.trim();
    if (!folderToken || !folderToken.includes(".")) {
      return undefined;
    }

    const subAssemblies = await getSubAssemblies();
    const matched = subAssemblies.find((item) => {
      const folderName = buildSubAssemblyFolderName(
        item.subAssemblyType,
        item.serialNumber
      );

      return folderName.localeCompare(folderToken, undefined, { sensitivity: "accent" }) === 0;
    });

    return matched?.id;
  };

  const resolveFolderTailNumber = (): string | null => {
    if (overrideTailNumber) {
      return overrideTailNumber;
    }

    if (lastSearchRequest?.TailNumber) {
      return lastSearchRequest.TailNumber;
    }

    for (const item of currentItems) {
      if ("tailNumber" in item && item.tailNumber) {
        return item.tailNumber;
      }
    }

    const activeFolderPath = navigationState.currentPath;
    if (activeFolderPath.length > 0) {
      const folder = findFolderByPath(activeFolderPath, localFolders);
      const folderDocuments = folder?.documents ?? [];
      const documentWithTailNumber = folderDocuments.find((document) => document.tailNumber);
      if (documentWithTailNumber?.tailNumber) {
        return documentWithTailNumber.tailNumber;
      }
    }

    return null;
  };

  const findFolderPathIndex = (targetFolder: Folder): number => {
    for (let index = 0; index < navigationState.currentPath.length; index++) {
      const currentPath = navigationState.currentPath.slice(0, index + 1);
      const matchedFolder = findFolderByPath(currentPath, localFolders);
      if (matchedFolder?.id === targetFolder.id) {
        return index;
      }
    }

    return -1;
  };

  const handleRenameFolderRequest = (folder: Folder) => {
    setFolderPendingRename(folder);
    setPendingFolderName(folder.name);
    setIsRenameFolderDialogOpen(true);
  };

  const handleRenameFolderConfirm = async () => {
    if (!folderPendingRename) {
      return;
    }

    const folderName = pendingFolderName.trim();
    if (!folderName) {
      showError("Folder name is required.");
      return;
    }

    const matchedPathIndex = findFolderPathIndex(folderPendingRename);

    try {
      setIsRenamingFolder(true);
      await renameCustomFolder({
        folderId: folderPendingRename.id,
        name: folderName,
      });
      await refreshFolderData();

      if (matchedPathIndex >= 0) {
        const updatedPath = [...navigationState.currentPath];
        updatedPath[matchedPathIndex] = folderName;
        // Replace so Back does not return to the pre-rename folder name.
        onNavigateBack(updatedPath, { replace: true });
      }

      showSuccess(`Folder "${folderName}" renamed successfully.`);
      closeRenameFolderDialog();
    } catch {
      showError("Failed to rename folder.");
    } finally {
      setIsRenamingFolder(false);
    }
  };

  const handleDeleteFolderRequest = (folder: Folder) => {
    setFolderPendingDelete(folder);
  };

  const handleDeleteFolderConfirm = async () => {
    if (!folderPendingDelete) {
      return;
    }

    const folderToDelete = folderPendingDelete;
    const matchedPathIndex = findFolderPathIndex(folderToDelete);

    try {
      setIsDeletingFolder(true);

      if (!isGuid(folderToDelete.id)) {
        showError("Invalid folder id. Unable to delete folder.");
        return;
      }

      await deleteCustomFolder({
        folderId: folderToDelete.id,
      });
      await refreshFolderData();

      if (matchedPathIndex >= 0) {
        // Replace so Back does not return into the deleted folder.
        onNavigateBack(navigationState.currentPath.slice(0, matchedPathIndex), {
          replace: true,
        });
      }

      showSuccess(`Folder "${folderToDelete.name}" deleted successfully.`);
      closeDeleteFolderDialog();
    } catch {
      showError("Failed to delete folder.");
    } finally {
      setIsDeletingFolder(false);
    }
  };

  const handleCreateFolderConfirm = async () => {
    const folderName = pendingFolderName.trim();
    if (!folderName) {
      showError("Folder name is required.");
      return;
    }

    const siblingFolders = folderPendingCreate?.children ?? sourceFolders;
    if (hasSiblingFolderName(siblingFolders, folderName)) {
      showError(`A folder named "${folderName}" already exists in this folder.`);
      return;
    }

    const parentCustomFolderId = folderPendingCreate && folderPendingCreate.code === "CUSTOM" && isGuid(folderPendingCreate.id)
      ? folderPendingCreate.id
      : undefined;
    const isAssemblyFolderCreate = isAssemblyScopedSearch(
      lastSearchRequest?.Filters?.documentScope
    );

    const attachedIataNodeKeyFromFolder = folderPendingCreate && folderPendingCreate.code !== "CUSTOM"
      ? (isAssemblyFolderCreate ? folderPendingCreate.id : folderPendingCreate.path)
      : undefined;

    // Inherit componentId / assemblyType from the folder we're creating under
    const componentId =
      folderPendingCreate?.componentId ??
      activeNavigationFolder?.componentId ??
      lastSearchRequest?.Filters?.componentId;
    const subAssemblyId =
      folderPendingCreate?.subAssemblyId ??
      activeNavigationFolder?.subAssemblyId ??
      lastSearchRequest?.Filters?.subAssemblyId;
    const assemblyType =
      folderPendingCreate?.assemblyType ??
      activeNavigationFolder?.assemblyType ??
      lastSearchRequest?.Filters?.assemblyType;
    const documentScope = lastSearchRequest?.Filters?.documentScope;
    const selectedAssemblyFolder = (lastSearchRequest?.Filters?.assemblyFolder ?? "").trim();
    const isSubAssemblyScope = documentScope === "subAssembly";

    let attachedIataNodeKey = attachedIataNodeKeyFromFolder;

    if (isSubAssemblyScope) {
      if (!folderPendingCreate || folderPendingCreate.code === "CUSTOM") {
        attachedIataNodeKey = undefined;
      } else if (isSubAssemblyRootFolderCode(folderPendingCreate.code)) {
        attachedIataNodeKey = folderPendingCreate.code;
      }
    }

    const normalizedAttachedIataNodeKey = (attachedIataNodeKey ?? "").trim();
    const shouldLookupSubAssemblyFromSelectedFolder =
      isSubAssemblyScope &&
      isSubAssemblyRootFolderCode(normalizedAttachedIataNodeKey);

    const subAssemblyLookupKey = shouldLookupSubAssemblyFromSelectedFolder
      ? selectedAssemblyFolder || undefined
      : normalizedAttachedIataNodeKey || selectedAssemblyFolder || undefined;

    const resolvedSubAssemblyId = await resolveSubAssemblyIdForCreate(
      subAssemblyId,
      assemblyType,
      subAssemblyLookupKey,
      isAssemblyFolderCreate
    );

    if (isSubAssemblyScope && !resolvedSubAssemblyId) {
      showError("Sub-assembly is required to create a custom folder in OffWing Sub-Assemblies.");
      return;
    }

   

    const tailNumber = resolveFolderTailNumber();
    if (!isAssemblyFolderCreate && !tailNumber) {
      showError("Tail number is required to create a custom folder.");
      return;
    }

    try {
      setIsCreatingFolder(true);
      await createCustomFolder({
        name: folderName,
        tailNumber: tailNumber ?? "",
        parentCustomFolderId,
        attachedIataNodeKey,
        componentId,
        subAssemblyId: resolvedSubAssemblyId,
        assemblyType,
      });
      await refreshFolderData();
      showSuccess(`Folder "${folderName}" created successfully.`);
      closeCreateFolderDialog();
    } catch {
      showError("Failed to create folder.");
    } finally {
      setIsCreatingFolder(false);
    }
  };

  const uploadFilesToFolder = async (
    files: File[],
    folder: Folder,
    showDropLoader = false
  ) => {
    if (isGroupedMode || !canCreateDocument) {
      return;
    }

    const firstDoc = folder.documents?.[0];
    const tailNumber = lastSearchRequest?.TailNumber || firstDoc?.tailNumber || overrideTailNumber;
    const isAssemblyScope = isAssemblyScopedSearch(
      lastSearchRequest?.Filters?.documentScope
    );
    const aircraftType = findAircraftTypeInTree(localFolders) || "";
    const assemblyType =
      folder.assemblyType ??
      activeNavigationFolder?.assemblyType ??
      lastSearchRequest?.Filters?.assemblyType ??
      "";
    const assemblyFolder = lastSearchRequest?.Filters?.assemblyFolder ?? "";
    const componentId = lastSearchRequest?.Filters?.componentId ?? "";

    if ((!isAssemblyScope && !tailNumber) || !folder.code) {
      showError("Missing required metadata (tail number or category).");
      return;
    }

    const existingFileNames = new Set(
      (folder.documents ?? [])
        .map((doc) => doc.fileName?.trim().toLowerCase())
        .filter((name): name is string => Boolean(name))
    );

    const duplicateFiles = files.filter((file) =>
      existingFileNames.has(file.name.trim().toLowerCase())
    );
    const filesToUpload = files.filter(
      (file) => !existingFileNames.has(file.name.trim().toLowerCase())
    );

    if (duplicateFiles.length > 0) {
      duplicateFiles.forEach((file) => {
        setUploadHistoryEntries(appendQuickUploadHistory(file.name, "fail"));
      });
      const duplicateNames = duplicateFiles.map((file) => file.name).join(", ");
      showError(
        duplicateFiles.length === 1
          ? `A file named "${duplicateNames}" already exists in this folder.`
          : `These files already exist in this folder: ${duplicateNames}`
      );
    }

    if (filesToUpload.length === 0) {
      return;
    }

    if (showDropLoader) {
      setIsDropUploading(true);
    }

    let anySucceeded = false;

    try {
      for (const file of filesToUpload) {
        const docWithFormNumber = folder.documents?.find(hasValidFormNumber);
        const existingFormNumber = docWithFormNumber?.title;
        const firstDocForFormNumber = folder.documents?.[0];
        const formNumber = lastSearchRequest?.Filters?.title || existingFormNumber || firstDocForFormNumber?.title || file.name;

        const metadata = {
          tailNumber: tailNumber ?? "",
          selectedFolderCode: resolveSelectedFolderCode(folder, isAssemblyScope),
          folderPath: folder.name,
          documentTypeCode: resolveDocumentTypeCode(folder),
          aircraftType,
          serialNumber: "",
          documentTitle: formNumber,
          description: "",
          isQuickUpload: true,
          assemblyType: isAssemblyScope ? assemblyType : undefined,
          assemblyFolder: isAssemblyScope ? assemblyFolder : undefined,
          assemblyCategory: isAssemblyScope
            ? resolveAssemblyCategory(folder, assemblyFolder)
            : undefined,
          componentId: isAssemblyScope && componentId ? componentId : undefined,
        };

        try {
          await uploadDocument(file, metadata);
          anySucceeded = true;
          setUploadHistoryEntries(appendQuickUploadHistory(file.name, "success"));
          showInfo(
            `Uploaded ${file.name} and it will go through document validation soon.`
          );
        } catch {
          setUploadHistoryEntries(appendQuickUploadHistory(file.name, "fail"));
          showError(`Failed to upload ${file.name}`);
        }
      }

      // Defer search refresh until the whole drop/upload batch finishes so each
      // upload invalidation does not trigger a mid-batch document-tree rebuild.
      if (anySucceeded) {
        await refreshFolderData();
      }
    } finally {
      if (showDropLoader) {
        setIsDropUploading(false);
      }
    }
  };

  const handleInlineUploadSelect = async (
    e: React.ChangeEvent<HTMLInputElement>,
    folder: Folder
  ) => {
    if (!e.target.files || e.target.files.length === 0) {
      return;
    }

    const files = Array.from(e.target.files);
    await uploadFilesToFolder(files, folder);
    e.target.value = "";
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    if (isGroupedMode || !canCreateDocument) return; // Disable drag-drop in grouped mode or without create permission
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    if (isGroupedMode || !canCreateDocument) return;
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (isGroupedMode || !canCreateDocument) return;
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    if (isGroupedMode || !canCreateDocument) return; // Disable drag-drop in grouped mode or without create permission
    setIsDragging(false);

    const currentFolder = findFolderByPath(
      navigationState.currentPath,
      localFolders
    );
    if (!currentFolder) return;

    const droppedFiles = Array.from(e.dataTransfer.files);

    if (!currentFolder.documents || currentFolder.documents.length === 0) {
      await handleFolderRowDrop(e, currentFolder);
      return;
    }
    await uploadFilesToFolder(droppedFiles, currentFolder, true);
  };

  // Type guard to check if item is Folder
  const isFolder = (item: Folder | Document): item is Folder => {
    return "children" in item;
  };
  const handleDownload = async (doc: Document) => {
    // Add file to downloading set to show loader
    setDownloadingFiles((prev) => new Set(prev).add(doc.id));

    try {
      await getBlob(`api/documents/${doc.id}/download`, {
        showLoader: true,
        showErrorSnackbar: false,
        showSuccessSnackbar: false,
        onSuccess: (blob, contentType) => {
          // Create download link and trigger download
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = doc.fileName;
          document.body.appendChild(link);
          link.click();
          link.remove();
          URL.revokeObjectURL(url);

          // Show success snackbar
          showSuccess(`File "${doc.fileName}" downloaded successfully`);
        },
        onError: (error) => {
          showError(
            `Failed to download "${doc.fileName}": ${
              error.message || "Unknown error"
            }`
          );
        },
        onFinally: () => {
          // Remove file from downloading set
          setDownloadingFiles((prev) => {
            const newSet = new Set(prev);
            newSet.delete(doc.id);
            return newSet;
          });
        },
      });

      // Still call the original function for any side effects
      onDownloadDocument(doc);
    } catch (error) {
      showError(
        `Failed to download "${doc.fileName}": ${(error as Error).message}`
      );
      // Remove file from downloading set on error
      setDownloadingFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(doc.id);
        return newSet;
      });
    }
  };

  // Function to get the appropriate FontAwesome icon based on mime type
  const getFileIcon = (mimeType: string) => {
    const type = (mimeType ?? "").toLowerCase();

    if (type.includes("word") || type.includes("doc")) {
      return faFileWord;
    } else if (
      type.includes("image") ||
      type.includes("jpg") ||
      type.includes("png") ||
      type.includes("gif") ||
      type.includes("jpeg") ||
      type.includes("tiff")
    ) {
      return faFileImage;
    } else if (type.includes("pdf")) {
      return faFilePdf;
    } else if (
      type.includes("excel") ||
      type.includes("xls") ||
      type.includes("spreadsheet")
    ) {
      return faFileExcel;
    } else {
      return faFile; // Default icon for unknown file types
    }
  };

  // Generate breadcrumb items based on current path
  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const breadcrumbs: BreadcrumbItem[] = [
      { label: "All Documents", path: [], isClickable: true },
    ];

    for (let index = 0; index < navigationState.currentPath.length; index++) {
      const pathItem = navigationState.currentPath[index];
      breadcrumbs.push({
        label: pathItem,
        path: navigationState.currentPath.slice(0, index + 1),
        isClickable: true,
      });
    }

    return breadcrumbs;
  };

  // Handle folder click - navigate into folder
  const handleFolderClick = (folder: Folder) => {
    onNavigateToFolder(folder.name);
  };

  // Handle document view
  const handleViewDocument = (doc: Document) => {
    window.open(`/document/search/${doc.id}`, "_blank", "noopener,noreferrer");
  };

  const findAircraftTypeInTree = (folders: Folder[]): string | null => {
    for (const folder of folders) {
      //  Check documents inside this folder
      if (folder.documents && folder.documents.length > 0) {
        const docWithType = folder.documents.find((d) => d.aircraftType);
        if (docWithType?.aircraftType) {
          return docWithType.aircraftType;
        }
      }

      //  Recursively check children
      if (folder.children && folder.children.length > 0) {
        const found = findAircraftTypeInTree(folder.children);
        if (found) return found;
      }
    }

    return null;
  };

  //handle drag drop on each folder record
  const handleFolderRowDrop = async (e: React.DragEvent, folder: Folder) => {
    if (isGroupedMode || !canCreateDocument) return; // Disable drag-drop in grouped mode or without create permission
    setIsDragging(false);
    setActiveDropFolder(null);
    const droppedFiles = Array.from(e.dataTransfer.files);

    await uploadFilesToFolder(droppedFiles, folder, true);
  };

  const shouldShowCreateFolderButton =
    canCreateDocument &&
    !isGroupedMode;

  const selectedDocumentScope = lastSearchRequest?.Filters?.documentScope as
    | "airframe"
    | "assembly"
    | "subAssembly"
    | undefined;
  const showDocumentStatusIcons = selectedDocumentScope === "airframe";
  const selectedTailNumber = (lastSearchRequest?.TailNumber ?? overrideTailNumber ?? "").trim();
  const selectedAssemblyType = (lastSearchRequest?.Filters?.assemblyType ?? "").trim();
  const selectedAssemblyFolder = (lastSearchRequest?.Filters?.assemblyFolder ?? "").trim();
  const isAtRootLevel = navigationState.currentPath.length === 0;
  const canDownloadSelectedAirframeRoot =
    canViewDocument &&
    !isGroupedMode &&
    isAtRootLevel &&
    selectedDocumentScope === "airframe" &&
    selectedTailNumber.length > 0;

  const canDownloadSelectedAssemblyRoot =
    canViewDocument &&
    !isGroupedMode &&
    isAtRootLevel &&
    (selectedDocumentScope === "assembly" || selectedDocumentScope === "subAssembly") &&
    selectedAssemblyType.length > 0 &&
    selectedAssemblyFolder.length > 0;

  const canDownloadSelectedRootArchive =
    canDownloadSelectedAirframeRoot || canDownloadSelectedAssemblyRoot;

  useEffect(() => {
    listExportJobsRef.current = listFolderExportJobs;
  }, [listFolderExportJobs]);

  const upsertExportJob = useCallback((incoming: FolderExportJobResponse) => {
    setActiveExportJobs((previous) => {
      const index = previous.findIndex((job) => job.jobId === incoming.jobId);

      if (!isFolderExportJobActive(incoming.status)) {
        if (index < 0) {
          return previous;
        }

        const next = [...previous];
        next.splice(index, 1);
        return next;
      }

      if (index >= 0) {
        const next = [...previous];
        next[index] = incoming;
        return next;
      }

      return [...previous, incoming];
    });
  }, []);

  useFolderExportSignalR({
    enabled: activeExportJobs.length > 0,
    onJobUpdated: upsertExportJob,
  });

  useEffect(() => {
    let cancelled = false;

    const refreshActiveExportJobs = async () => {
      try {
        const jobs = await listExportJobsRef.current(100);
        if (cancelled) {
          return;
        }

        setActiveExportJobs(jobs.filter((job) => isFolderExportJobActive(job.status)));
      } catch {
        if (!cancelled) {
          setActiveExportJobs([]);
        }
      }
    };

    void refreshActiveExportJobs();

    return () => {
      cancelled = true;
    };
  }, [
    selectedDocumentScope,
    selectedTailNumber,
    selectedAssemblyType,
    selectedAssemblyFolder,
  ]);

  const rootExportLabel = canDownloadSelectedAirframeRoot
    ? buildAirframeRootExportLabel(selectedTailNumber)
    : canDownloadSelectedAssemblyRoot
      ? buildAssemblyRootExportLabel(selectedAssemblyType, selectedAssemblyFolder)
      : "";

  const activeRootExportJob = rootExportLabel
    ? findActiveExportJobByLabel(activeExportJobs, rootExportLabel)
    : undefined;

  const isRootExportBlocked = Boolean(activeRootExportJob);

  const isFolderExportBlocked = (folder: Folder): boolean => {
    const isAssemblyScopedExport =
      selectedDocumentScope === "assembly" || selectedDocumentScope === "subAssembly";

    const expectedLabel = buildFolderPathExportLabel({
      folderPath: folder.path,
      tailNumber:
        selectedDocumentScope === "airframe" ? selectedTailNumber : undefined,
      assemblyType:
        isAssemblyScopedExport ? selectedAssemblyType : undefined,
      assemblyFolder:
        isAssemblyScopedExport ? selectedAssemblyFolder : undefined,
    });

    return activeExportJobs.some((job) => jobMatchesExportLabel(job, expectedLabel));
  };

  const trackQueuedExportJob = (job: FolderExportJobResponse) => {
    if (isFolderExportJobActive(job.status)) {
      upsertExportJob(job);
    }
  };

  const handleDownloadSelectedRootArchive = async () => {
    if (!canDownloadSelectedRootArchive) {
      return;
    }

    if (isRootExportBlocked) {
      showInfo(FOLDER_EXPORT_IN_PROGRESS_TOOLTIP);
      return;
    }

    try {
      setIsDownloadingRootArchive(true);

      const job = await startFolderExportJob(
        canDownloadSelectedAirframeRoot
          ? {
              downloadScope: "airframe-root",
              tailNumber: selectedTailNumber,
            }
          : {
              downloadScope: "warehouse-offwing-assembly-root",
              assemblyType: selectedAssemblyType,
              assemblyFolder: selectedAssemblyFolder,
            }
      );

      if (!job) {
        return;
      }

      trackQueuedExportJob(job);

      if (job.status === 2) {
        const started = await triggerExportJobDownloads(job, getFolderExportJob);
        if (started > 0) {
          showInfo(getExportDownloadStartMessage(started));
          return;
        }

        showInfo("An existing export is ready. Opening Export Jobs page.");
        navigate("/document/exports");
        return;
      }

      const targetLabel = canDownloadSelectedAirframeRoot
        ? selectedTailNumber
        : selectedAssemblyFolder;
      showSuccess(
        `Export queued for ${targetLabel}. Open Export Jobs page to download when it is ready.`
      );
      navigate("/document/exports");
    } catch (error) {
      showError(
        `Failed to queue root folder export: ${(error as Error).message}`
      );
    } finally {
      setIsDownloadingRootArchive(false);
    }
  };

  const handleDownloadFolderArchive = async (folder: Folder) => {
    if (!canViewDocument || folder.totalDocumentCount === 0) {
      return;
    }

    if (isFolderExportBlocked(folder)) {
      showInfo(FOLDER_EXPORT_IN_PROGRESS_TOOLTIP);
      return;
    }

    const isAssemblyScopedExport =
      selectedDocumentScope === "assembly" || selectedDocumentScope === "subAssembly";

    setDownloadingFolders((prev) => new Set(prev).add(folder.id));

    try {
      const job = await startFolderExportJob({
        downloadScope: "folder-path",
        folderPath: folder.path,
        archiveRootName: folder.name,
        tailNumber:
          selectedDocumentScope === "airframe" ? selectedTailNumber : undefined,
        assemblyType:
          isAssemblyScopedExport ? selectedAssemblyType : undefined,
        assemblyFolder:
          isAssemblyScopedExport ? selectedAssemblyFolder : undefined,
      });

      if (!job) {
        return;
      }

      trackQueuedExportJob(job);

      if (job.status === 2) {
        const started = await triggerExportJobDownloads(job, getFolderExportJob);
        if (started > 0) {
          showInfo(getExportDownloadStartMessage(started));
          return;
        }

        showInfo(`An existing export for ${folder.name} is ready. Open Export Jobs page to download.`);
        return;
      }

      showInfo(`Export queued for ${folder.name}. Download will be available on Export Jobs page.`);
      navigate("/document/exports");
    } catch (error) {
      showError(`Failed to queue folder export: ${(error as Error).message}`);
    } finally {
      setDownloadingFolders((prev) => {
        const next = new Set(prev);
        next.delete(folder.id);
        return next;
      });
    }
  };

  return (
    <Paper
      sx={{
        height: "calc(100%-32px)",
        p: 2,
        border: "1px solid white",
        background: "none",
        position: "relative",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          mb: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: { xs: 0.5, sm: 1 },
            alignItems: "center",
            justifyContent: { xs: "flex-start", md: "flex-end" },
            rowGap: { xs: 1, sm: 0.5 },
          }}
        >
          <Box sx={{ minHeight: 44, display: "flex", alignItems: "center" }}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={hideEmptyFolders}
                  onChange={(e) => setHideEmptyFolders(e.target.checked)}
                  sx={{
                    width: 52,
                    height: 30,
                    padding: 0,
                    "& .MuiSwitch-switchBase": {
                      padding: 1,
                      color: "rgba(255,255,255,0.9)",
                      "&.Mui-checked": {
                        transform: "translateX(22px)",
                        color: "#fff",
                      },
                    },
                    "& .MuiSwitch-track": {
                      borderRadius: 20,
                      backgroundColor: "rgba(255,255,255,0.28)",
                      opacity: 1,
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "hsl(19 101% 51% / 1)",
                      opacity: 1,
                    },
                  }}
                />
              }
              label="Hide empty folders"
              sx={{
                m: 0,
                "& .MuiFormControlLabel-label": {
                  color: "#fff",
                  fontSize: "0.9rem",
                  marginLeft: "0.5rem",
                },
              }}
            />
          </Box>
          {(hasDocumentsInView || hasFoldersInView) && canMoveDocuments && (
            <Box sx={{ minHeight: 44, display: "flex", alignItems: "center" }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={isMoveMode}
                    onChange={(e) => {
                      const enabled = e.target.checked;
                      setIsMoveMode(enabled);
                      if (!enabled) {
                        clearDocumentSelection();
                      }
                    }}
                    sx={{
                      width: 52,
                      height: 30,
                      padding: 0,
                      "& .MuiSwitch-switchBase": {
                        padding: 1,
                        color: "rgba(255,255,255,0.9)",
                        "&.Mui-checked": {
                          transform: "translateX(22px)",
                          color: "#fff",
                        },
                      },
                      "& .MuiSwitch-track": {
                        borderRadius: 20,
                        backgroundColor: "rgba(255,255,255,0.28)",
                        opacity: 1,
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        backgroundColor: BRAND_ORANGE,
                        opacity: 1,
                      },
                    }}
                  />
                }
                label={moveModeLabel}
                sx={{
                  m: 0,
                  "& .MuiFormControlLabel-label": {
                    color: "#fff",
                    fontSize: "0.9rem",
                    marginLeft: "0.5rem",
                  },
                }}
              />
            </Box>
          )}
          {!isGroupedMode && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<CompareArrowsIcon sx={{ fontSize: "1.1rem !important" }} />}
              onClick={() => setCompareDialogOpen(true)}
              sx={{
                color: "hsl(19 101% 51% / 1)",
                borderColor: "hsl(19 101% 51% / 1)",
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              Compare
            </Button>
          )}
          {!isGroupedMode && canCreateDocument && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<HistoryIcon sx={{ fontSize: "1.1rem !important" }} />}
              onClick={() => {
                setUploadHistoryEntries(readQuickUploadHistory());
                setUploadHistoryOpen(true);
              }}
              sx={{
                color: "hsl(19 101% 51% / 1)",
                borderColor: "hsl(19 101% 51% / 1)",
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              Upload activity
            </Button>
          )}
          {canDownloadSelectedRootArchive && (
            <Tooltip
              title={
                isRootExportBlocked
                  ? FOLDER_EXPORT_IN_PROGRESS_TOOLTIP
                  : `Export all documents at root level`
              }
              arrow
            >
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon sx={{ fontSize: "1.1rem !important" }} />}
                  onClick={handleDownloadSelectedRootArchive}
                  disabled={isDownloadingRootArchive || isRootExportBlocked}
                  sx={{
                    color: "hsl(19 101% 51% / 1)",
                    borderColor: "hsl(19 101% 51% / 1)",
                    textTransform: "none",
                    fontWeight: 700,
                    "&.Mui-disabled": {
                      color: "hsl(19 101% 51% / 0.7)",
                      borderColor: "hsl(19 101% 51% / 0.45)",
                      opacity: 1,
                      cursor: "not-allowed",
                    },
                    "&.Mui-disabled .MuiButton-startIcon": {
                      color: "hsl(19 101% 51% / 0.7)",
                    },
                  }}
                >
                  {`Export ${(canDownloadSelectedAirframeRoot ? selectedTailNumber : selectedAssemblyFolder)} Root`}
                </Button>
              </span>
            </Tooltip>
          )}
          {shouldShowCreateFolderButton && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleCreateFolderRequest}
              sx={{
                color: "hsl(19 101% 51% / 1)",
                borderColor: "hsl(19 101% 51% / 1)",
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              Create Folder
            </Button>
          )}
        </Box>
        <Box sx={{ width: "100%" }}>
          <Breadcrumb items={generateBreadcrumbs()} onNavigate={onNavigateBack} />
        </Box>
      </Box>
      {activeMoveJob && (
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 0.5, sm: 2 },
            p: 1.5,
            mb: 1,
            backgroundColor: "#1a2a1a",
            border: "1px solid #2d5a2d",
            borderRadius: 1,
            color: "#fff",
          }}
        >
          {isTerminalMoveStatus(activeMoveJob.status) ? (
            activeMoveJob.status === "Completed" ? (
              <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 18 }} />
            ) : (
              <ErrorOutlineIcon sx={{ color: "#f44336", fontSize: 18 }} />
            )
          ) : (
            <CircularProgress size={16} sx={{ color: "#4caf50" }} />
          )}
          <Typography sx={{ flex: 1, fontSize: "0.875rem" }}>
            {isTerminalMoveStatus(activeMoveJob.status)
              ? activeMoveJob.status === "Completed"
                ? `Move complete — files moved to '${activeMoveJob.targetFolderName}'`
                : `Move ${activeMoveJob.status === "Failed" ? "failed" : "finished with errors"} — files to '${activeMoveJob.targetFolderName}'`
              : `Moving files to '${activeMoveJob.targetFolderName}'`}
            {" "}
            — {activeMoveJob.completedFileCount} of {activeMoveJob.totalFileCount} complete
            {activeMoveJob.failedFileCount > 0 &&
              ` (${activeMoveJob.failedFileCount} failed)`}
          </Typography>
          <Button
            size="small"
            variant="text"
            onClick={() => navigate("/document/move-jobs")}
            sx={{
              color: "hsl(19, 101%, 51%)",
              textTransform: "none",
              whiteSpace: "nowrap",
            }}
          >
            View move jobs
          </Button>
        </Box>
      )}
      {(() => {
        const totalSelected =
          selectedDocIds.size + selectedFolderIds.size;
        const currentDocs = getCurrentDocuments();
        const currentFolders = getCurrentFolders();
        const selectableFolders = currentFolders.filter((f) => isCustomFolder(f));
        const allSelected =
          selectedDocIds.size === currentDocs.length
          && selectedFolderIds.size === selectableFolders.length
          && totalSelected > 0;
        const selectionLabel =
          selectedDocIds.size > 0 && selectedFolderIds.size > 0
            ? `${selectedDocIds.size} file${selectedDocIds.size === 1 ? "" : "s"} and ${selectedFolderIds.size} folder${selectedFolderIds.size === 1 ? "" : "s"} selected`
            : selectedDocIds.size > 0
              ? `${selectedDocIds.size} file${selectedDocIds.size === 1 ? "" : "s"} selected`
              : `${selectedFolderIds.size} folder${selectedFolderIds.size === 1 ? "" : "s"} selected`;
        const moveButtonLabel =
          selectedFolderIds.size > 0 && selectedDocIds.size === 0
            ? selectedFolderIds.size === 1
              ? "Move folder"
              : "Move folders"
            : "Move to folder";

        return isMoveMode && totalSelected > 0 ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
            rowGap: { xs: 0.5, sm: 0 },
            mb: 1.5,
            px: 1.5,
            py: 1,
            backgroundColor: "#1a1a1a",
            border: "1px solid #333",
            borderRadius: 1,
          }}
        >
          <Typography sx={{ color: "#fff", fontWeight: 700, flexShrink: 0 }}>
            {selectionLabel}
          </Typography>
          <Button
            size="small"
            onClick={() => {
              if (allSelected) {
                clearDocumentSelection();
              } else {
                setSelectedDocIds(
                  new Set(currentDocs.map((d) => d.id))
                );
                setSelectedFolderIds(
                  new Set(selectableFolders.map((f) => f.id))
                );
              }
            }}
            sx={{
              color: BRAND_ORANGE,
              textTransform: "none",
              fontSize: "0.8rem",
              padding: "2px 6px",
            }}
          >
            {allSelected ? "Deselect all" : "Select all"}
          </Button>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              marginLeft: "auto",
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={openMoveDrawer}
              sx={{
                color: BRAND_ORANGE,
                borderColor: BRAND_ORANGE,
                textTransform: "none",
                "&:hover": {
                  borderColor: BRAND_ORANGE,
                  backgroundColor: BRAND_ORANGE_HOVER,
                },
              }}
            >
              {moveButtonLabel}
            </Button>
            <Button
              size="small"
              onClick={clearDocumentSelection}
              sx={{ color: "#fff", textTransform: "none" }}
            >
              Clear
            </Button>
          </Box>
        </Box>
        ) : null;
      })()}
      <Box
        sx={{ position: "relative" }}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {isDragging && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
              border: "3px dashed white",
              zIndex: 20,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontSize: "1.4rem",
              fontWeight: "bold",
              color: "white",
              pointerEvents: "none",
            }}
          >
            Drop files to upload
          </Box>
        )}
        {isDropUploading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.6)",
              zIndex: 21,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              gap: 2,
              color: "white",
            }}
          >
            <CircularProgress size={30} sx={{ color: "hsl(19 101% 51% / 1)" }} />
            <Typography sx={{ fontWeight: 700 }}>Uploading document...</Typography>
          </Box>
        )}
        {currentItems.length === 0 ? (
          navigationState.currentFolder && canCreateDocument ? (
            <Box
              component="label"
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                gap: 1,
                height: "200px",
                border: "2px dashed #fff",
                borderRadius: 1,
                color: "#fff",
                fontSize: "1.2rem",
                fontWeight: "bold",
                cursor: "pointer",
              }}
            >
              <Typography sx={{ fontWeight: 700 }}>Drag and drop documents here</Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.72)", fontSize: "0.92rem" }}>
                or click to browse and upload files into this folder.
              </Typography>
              <input
                type="file"
                multiple
                hidden
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.tif,.tiff,.xml,.xmls,.wmv,.mp4,.avi,.mpeg,.zip,.html"
                onChange={(e) => handleInlineUploadSelect(e, navigationState.currentFolder as Folder)}
              />
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                gap: 1,
                height: "200px",
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: 1,
                color: "#fff",
                fontSize: "1rem",
              }}
            >
              <Typography sx={{ fontWeight: 700 }}>No documents or folders found.</Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.72)", fontSize: "0.92rem" }}>
                Adjust the filters or toggle off hidden folders to refresh the view.
              </Typography>
            </Box>
          )
        ) : (
          <TableContainer sx={{ maxHeight: "calc(100% - 96px)" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    padding="none"
                    sx={{
                      fontWeight: "bold",
                      textAlign: "center",
                      color: "#fff",
                    }}
                  >
                    Icon
                  </TableCell>
                  <TableCell
                    padding="none"
                    sx={{
                      fontWeight: "bold",
                      textAlign: "center",
                      color: "#fff",
                    }}
                  >
                    Name
                  </TableCell>
                  <TableCell
                    padding="none"
                    sx={{
                      fontWeight: "bold",
                      textAlign: "center",
                      color: "#fff",
                    }}
                  >
                    Type
                  </TableCell>
                  <TableCell
                    padding="none"
                    sx={{
                      fontWeight: "bold",
                      textAlign: "center",
                      color: "#fff",
                    }}
                  >
                    Size/Details
                  </TableCell>
                  <TableCell
                    padding="none"
                    sx={{
                      fontWeight: "bold",
                      textAlign: "center",
                      color: "#fff",
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentItems.map((item, index) => {
                  if (isFolder(item)) {
                    // It's a folder
                    const folder = item;
                    const canManageFolder = isCustomFolder(folder);
                    const isPendingMoveFolder = lockedFolderIds.has(folder.id);
                    const isFolderBlocked = isPendingMoveFolder && onMoveSourcePage;
                    const showFolderMoving =
                      isPendingMoveFolder
                      && (isFolderBlocked || isMoveInProgress);
                    return (
                      <TableRow
                        key={folder.id}
                        hover={!isFolderBlocked}
                        onContextMenu={(e) => {
                          if (isFolderBlocked) {
                            e.preventDefault();
                          }
                        }}
                        onClick={(e) => {
                          if (isFolderBlocked) {
                            e.preventDefault();
                            e.stopPropagation();
                          }
                        }}
                        onDragEnter={(e) => {
                          e.preventDefault();
                          if (!canCreateDocument || isFolderBlocked) return;
                          setIsDragging(true);
                          setActiveDropFolder(folder);
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          if (!canCreateDocument || isFolderBlocked) return;
                          setActiveDropFolder(folder);
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          if (activeDropFolder?.id === folder.id) {
                            setActiveDropFolder(null);
                          }
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!canCreateDocument || isFolderBlocked) return;
                          handleFolderRowDrop(e, folder);
                        }}
                        sx={{
                          background:
                            activeDropFolder?.id === folder.id && canCreateDocument && !isFolderBlocked
                              ? "hsl(19 101% 51% / 1)"
                              : "transparent",
                          backgroundColor:
                            selectedFolderIds.has(folder.id) &&
                            isMoveMode &&
                            isCustomFolder(folder)
                              ? "rgba(230, 81, 0, 0.25) !important"
                              : "transparent",
                          border:
                            activeDropFolder?.id === folder.id && canCreateDocument && !isFolderBlocked
                              ? "2px dashed #fff"
                              : "none",
                          opacity: isFolderBlocked ? 0.45 : 1,
                          cursor: isFolderBlocked ? "not-allowed" : undefined,
                        }}
                      >
                        <TableCell
                          padding="none"
                          sx={{ textAlign: "center", py: 1 }}
                        >
                          <FolderIcon sx={{ color: "hsl(19 101% 51% / 1)" }} />
                        </TableCell>
                        <TableCell sx={{ color: "#fff", py: 1 }} padding="none">
                          <Box
                            component="button"
                            sx={{
                              fontWeight: 700,
                              color: "#fff",
                              cursor: isFolderBlocked ? "not-allowed" : "pointer",
                              background: "none",
                              border: "none",
                              textAlign: "left",
                              fontSize: "inherit",
                              "&:hover": { textDecoration: isFolderBlocked ? "none" : "underline" },
                            }}
                            onClick={() => {
                              if (isFolderBlocked) return;
                              handleFolderClick(folder);
                            }}
                          >
                            {folder.name}
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{ color: "#fff", py: 1 }}
                          padding="none"
                          align="center"
                        >
                          Folder
                        </TableCell>
                        <TableCell
                          sx={{ color: "#fff", py: 1 }}
                          padding="none"
                          align="center"
                        >
                          {folder.totalDocumentCount} documents
                        </TableCell>
                        <TableCell
                          sx={{ color: "#fff", py: 1 }}
                          padding="none"
                          align="center"
                        >
                          {showFolderMoving ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 0.5,
                                color: isFolderBlocked
                                  ? "rgba(255,255,255,0.5)"
                                  : "hsl(19 101% 51% / 1)",
                              }}
                            >
                              <CircularProgress
                                size={12}
                                sx={{
                                  color: isFolderBlocked
                                    ? "rgba(255,255,255,0.5)"
                                    : "hsl(19 101% 51% / 1)",
                                }}
                              />
                              <Typography variant="caption">
                                {isFolderBlocked ? "Moving..." : "Arriving..."}
                              </Typography>
                            </Box>
                          ) : (
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: isMoveMode
                                ? "40px 40px 40px 40px 40px"
                                : "40px 40px 40px 40px",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            {canManageFolder && folder.totalDocumentCount === 0 ? (
                              <IconButton
                                sx={{ color: "#fff" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRenameFolderRequest(folder);
                                }}
                                title="Rename folder"
                                aria-label={`Edit folder ${folder.name}`}
                              >
                                <DriveFileRenameOutlineIcon />
                              </IconButton>
                            ) : (
                              <Box sx={{ width: 40, height: 40 }} />
                            )}

                            {canManageFolder && folder.totalDocumentCount === 0 ? (
                              <IconButton
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteFolderRequest(folder);
                                }}
                                title="Delete folder"
                                aria-label={`Delete folder ${folder.name}`}
                              >
                                <DeleteOutlineIcon />
                              </IconButton>
                            ) : (
                              <Box sx={{ width: 40, height: 40 }} />
                            )}

                            <IconButton
                              sx={{ color: "#fff" }}
                              onClick={() => handleFolderClick(folder)}
                              title="Open folder"
                              aria-label={`Open folder ${folder.name}`}
                            >
                              <VisibilityIcon />
                            </IconButton>

                            {canViewDocument && folder.totalDocumentCount > 0 ? (
                              <Tooltip
                                title={
                                  isFolderExportBlocked(folder)
                                    ? FOLDER_EXPORT_IN_PROGRESS_TOOLTIP
                                    : "Export folder"
                                }
                                arrow
                              >
                                <span>
                                  <IconButton
                                    sx={{ color: "#fff" }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownloadFolderArchive(folder);
                                    }}
                                    aria-label={`Export folder ${folder.name}`}
                                    disabled={
                                      downloadingFolders.has(folder.id)
                                      || isFolderExportBlocked(folder)
                                    }
                                  >
                                    {downloadingFolders.has(folder.id) ? (
                                      <CircularProgress size={18} sx={{ color: "#fff" }} />
                                    ) : (
                                      <DownloadIcon />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : (
                              <Box sx={{ width: 40, height: 40 }} />
                            )}

                            {isMoveMode && isCustomFolder(folder) && (
                              <Checkbox
                                checked={selectedFolderIds.has(folder.id)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  const newSet = new Set(selectedFolderIds);
                                  if (e.target.checked) {
                                    newSet.add(folder.id);
                                  } else {
                                    newSet.delete(folder.id);
                                  }
                                  setSelectedFolderIds(newSet);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                size="small"
                                sx={{
                                  color: "#fff",
                                  padding: { xs: "8px", sm: "4px" },
                                  "&.Mui-checked": {
                                    color: "hsl(19, 101%, 51%)",
                                  },
                                }}
                              />
                            )}
                          </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  } else {
                    // It's a document
                    const doc = item;
                    const isSelected = selectedDocIds.has(doc.id);
                    const isPendingMoveDoc = lockedDocIds.has(doc.id);
                    const isDocBlocked = isPendingMoveDoc && onMoveSourcePage;
                    const showDocMoving =
                      isPendingMoveDoc
                      && (isDocBlocked || isMoveInProgress);
                    return (
                      <TableRow
                        key={doc.id}
                        hover={!isDocBlocked && (!isSelected || !isMoveMode)}
                        sx={{
                          backgroundColor:
                            isSelected && isMoveMode
                              ? `${BRAND_ORANGE_SELECTED_BG} !important`
                              : "transparent",
                          "&.MuiTableRow-root": {
                            backgroundColor:
                              isSelected && isMoveMode
                                ? `${BRAND_ORANGE_SELECTED_BG} !important`
                                : undefined,
                          },
                          "&:hover": {
                            backgroundColor:
                              isSelected && isMoveMode
                                ? `${BRAND_ORANGE_SELECTED_HOVER} !important`
                                : undefined,
                          },
                          opacity: isDocBlocked ? 0.45 : 1,
                          cursor: isDocBlocked
                            ? "not-allowed"
                            : isMoveMode
                              ? "pointer"
                              : "default",
                          userSelect: "none",
                        }}
                      >
                        <TableCell
                          padding="none"
                          sx={{ textAlign: "center", py: 1 }}
                        >
                          <FontAwesomeIcon
                            icon={getFileIcon(doc.mimeType)}
                            style={{ color: "#fff", fontSize: "1.2rem" }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: "#fff", py: 1 }} padding="none">
                          {doc.fileName}
                        </TableCell>
                        <TableCell
                          sx={{ color: "#fff", py: 1 }}
                          padding="none"
                          align="center"
                        >
                          {doc.mimeType}
                        </TableCell>
                        <TableCell
                          sx={{ color: "#fff", py: 1 }}
                          padding="none"
                          align="center"
                        >
                          {(doc.sizeBytes / 1024).toFixed(2)} KB
                        </TableCell>
                        <TableCell
                          sx={{ color: "#fff", py: 1 }}
                          padding="none"
                          align="center"
                        >
                          {showDocMoving ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1,
                              }}
                            >
                              <CircularProgress
                                size={14}
                                sx={{
                                  color: isDocBlocked ? "rgba(255,255,255,0.5)" : "#4caf50",
                                }}
                              />
                              <Typography sx={{ color: "#fff", fontSize: "0.8rem" }}>
                                {isDocBlocked ? "Moving..." : "Arriving..."}
                              </Typography>
                            </Box>
                          ) : (
                            <>
                          {showDocumentStatusIcons && doc.status === "UnderReview" && (
                            <Tooltip title="Document is under review" arrow>
                              <IconButton color="info" onClick={(e) => e.stopPropagation()}>
                                <ErrorOutlineIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {showDocumentStatusIcons && doc.status === "FailedReview" && canEditDocument && (
                            <Tooltip
                              title="Document failed review - click to fix"
                              arrow
                            >
                              <IconButton
                                sx={{ color: "#ff9800" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.currentTarget.blur();
                                  setSelectedFailedDoc(doc);
                                  setShowFixModal(true);
                                }}
                              >
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {showDocumentStatusIcons &&
                            (doc.status === "Approved" ||
                            doc.status === "Draft") && (
                            <Tooltip title="Document uploaded successfully" arrow>
                              <IconButton color="success" onClick={(e) => e.stopPropagation()}>
                                <CheckCircleIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canViewDocument && (
                            <>
                              <Tooltip
                                title={
                                  isHtmlDocument(doc.fileName, doc.mimeType)
                                    ? "Preview is not available for HTML files. Use Download."
                                    : isArchiveDocument(doc.fileName, doc.mimeType)
                                    ? "Preview is not available for archive files. Use Download."
                                    : "View"
                                }
                                arrow
                              >
                                <span>
                                  <IconButton
                                    sx={{
                                      color: "#fff",
                                      // Keep the eye visible on dark rows when disabled (zip, etc.).
                                      "&.Mui-disabled": {
                                        color: "rgba(255, 255, 255, 0.4)",
                                      },
                                    }}
                                    onClick={() => handleViewDocument(doc)}
                                    disabled={
                                      isArchiveDocument(
                                        doc.fileName,
                                        doc.mimeType
                                      ) ||
                                      isHtmlDocument(doc.fileName, doc.mimeType)
                                    }
                                    aria-label={
                                      isHtmlDocument(doc.fileName, doc.mimeType)
                                        ? "View unavailable for HTML files"
                                        : isArchiveDocument(doc.fileName, doc.mimeType)
                                        ? "View unavailable for archive files"
                                        : "View"
                                    }
                                  >
                                    <VisibilityIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <IconButton
                                sx={{ color: "#fff" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownload(doc);
                                }}
                                disabled={downloadingFiles.has(doc.id)}
                              >
                                <DownloadIcon />
                              </IconButton>
                            </>
                          )}
                          {canDeleteDocument && (
                            <IconButton
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteDocument(doc);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                          <Tooltip
                            title={
                              <span style={{ whiteSpace: "pre-line" }}>
                                {formatDocumentUploadInfo(doc.createdBy, doc.createdAt).tooltip}
                              </span>
                            }
                            arrow
                          >
                            <IconButton
                              sx={{ color: "#fff" }}
                              onClick={(e) => e.stopPropagation()}
                              aria-label={`Upload info for ${doc.fileName}`}
                            >
                              <InfoOutlinedIcon />
                            </IconButton>
                          </Tooltip>
                          {isMoveMode && (
                            <Checkbox
                              checked={selectedDocIds.has(doc.id)}
                              disabled={isDocBlocked}
                              onChange={(e) => {
                                e.stopPropagation();
                                const currentDocs = getCurrentDocuments();
                                const currentIndex = currentDocs.findIndex(
                                  (d) => d.id === doc.id
                                );

                                if (
                                  e.nativeEvent instanceof MouseEvent &&
                                  (e.nativeEvent as MouseEvent).shiftKey &&
                                  lastCheckedIndex.current !== -1
                                ) {
                                  const start = Math.min(
                                    lastCheckedIndex.current,
                                    currentIndex
                                  );
                                  const end = Math.max(
                                    lastCheckedIndex.current,
                                    currentIndex
                                  );
                                  const newSet = new Set(selectedDocIds);
                                  currentDocs.slice(start, end + 1).forEach((d) => {
                                    if (e.target.checked) {
                                      newSet.add(d.id);
                                    } else {
                                      newSet.delete(d.id);
                                    }
                                  });
                                  setSelectedDocIds(newSet);
                                } else {
                                  const newSet = new Set(selectedDocIds);
                                  if (e.target.checked) {
                                    newSet.add(doc.id);
                                  } else {
                                    newSet.delete(doc.id);
                                  }
                                  setSelectedDocIds(newSet);
                                }
                                lastCheckedIndex.current = currentIndex;
                              }}
                              onClick={(e) => e.stopPropagation()}
                              size="small"
                              sx={{
                                color: "#fff",
                                padding: { xs: "8px", sm: "4px" },
                                "&.Mui-checked": {
                                  color: BRAND_ORANGE,
                                },
                              }}
                            />
                          )}
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  }
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
      {showFixModal && (
        <FailedDocumentModal
          doc={selectedFailedDoc}
          onClose={() => setShowFixModal(false)}
          refresh={refreshFolderData}
        />
      )}
      <Dialog
        open={isCreateFolderDialogOpen}
        onClose={closeCreateFolderDialog}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: dialogPaperSx,
        }}
      >
        <DialogTitle sx={dialogTitleSx}>Create Folder</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography sx={{ color: "#ccc", mt: 1 }}>
            Enter a name for the new folder in the current location.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Folder name"
            value={pendingFolderName}
            onChange={(e) => setPendingFolderName(e.target.value)}
            fullWidth
            variant="outlined"
            sx={dialogTextFieldSx}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleCreateFolderConfirm();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateFolderDialog} sx={{ color: "#fff" }} disabled={isCreatingFolder}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateFolderConfirm}
            variant="contained"
            sx={dialogPrimaryButtonSx}
            disabled={isCreatingFolder}
          >
            {isCreatingFolder ? "Creating..." : "Create Folder"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={isRenameFolderDialogOpen}
        onClose={closeRenameFolderDialog}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: dialogPaperSx,
        }}
      >
        <DialogTitle sx={dialogTitleSx}>Rename Folder</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography sx={{ color: "#ccc", mt: 1 }}>
            Update the folder name for the current location.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Folder name"
            value={pendingFolderName}
            onChange={(e) => setPendingFolderName(e.target.value)}
            fullWidth
            variant="outlined"
            sx={dialogTextFieldSx}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleRenameFolderConfirm();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRenameFolderDialog} sx={{ color: "#fff" }} disabled={isRenamingFolder}>
            Cancel
          </Button>
          <Button
            onClick={handleRenameFolderConfirm}
            variant="contained"
            sx={dialogPrimaryButtonSx}
            disabled={isRenamingFolder}
          >
            {isRenamingFolder ? "Renaming..." : "Rename Folder"}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={folderPendingDelete !== null}
        title="Delete Folder"
        message={`Delete folder "${folderPendingDelete?.name ?? ""}"? This will archive the folder in the backend.`}
        confirmLabel="Delete Folder"
        cancelLabel="Cancel"
        isDangerous={true}
        onConfirm={handleDeleteFolderConfirm}
        onCancel={closeDeleteFolderDialog}
        isLoading={isDeletingFolder}
      />
      <CompareDirectoryDialog
        open={compareDialogOpen}
        onClose={() => setCompareDialogOpen(false)}
        appFolders={activeNavigationFolder ? activeNavigationFolder.children : sourceFolders}
        scopeDocuments={activeNavigationFolder?.documents}
        scopeLabel={activeNavigationFolder?.name ?? null}
        tailNumber={resolveFolderTailNumber()}
      />
      {/* Folder picker for confirmed document moves */}
      <MoveFolderPickerDrawer
        open={isMoveDrawerOpen}
        onClose={() => setIsMoveDrawerOpen(false)}
        folders={allFolders ?? localFolders}
        currentFolderPath={navigationState.currentPath}
        selectedCount={selectedDocIds.size}
        movingFolderIds={selectedFolderIds}
        airframeId={airframeId?.trim() || undefined}
        tailNumber={
          lastSearchRequest?.TailNumber ??
          overrideTailNumber ??
          resolveFolderTailNumber() ??
          undefined
        }
        onFolderCreated={onRefresh
          ? async () => {
              await onRefresh();
              setIsMoveDrawerOpen(true);
              setIsMoveMode(true);
            }
          : undefined}
        onConfirm={handleMoveConfirm}
      />
      <FolderConflictDialog
        open={isConflictDialogOpen}
        folderName={
          Array.from(selectedFolderIds)[0]
            ? (getCurrentFolders().find(
                (f) => f.id === Array.from(selectedFolderIds)[0]
              )?.name ?? "folder")
            : "folder"
        }
        targetFolderName={pendingMoveParams?.targetFolderName ?? ""}
        onResolve={handleConflictResolution}
        onCancel={() => {
          setIsConflictDialogOpen(false);
          setPendingMoveParams(null);
        }}
      />
      <Modal
        open={uploadHistoryOpen}
        onClose={() => setUploadHistoryOpen(false)}
        aria-labelledby="quick-upload-history-title"
        hideBackdrop
        disableScrollLock
        sx={{
          pointerEvents: "none",
        }}
      >
        <Box
          role="dialog"
          aria-modal="true"
          aria-labelledby="quick-upload-history-title"
          sx={{
            pointerEvents: "auto",
            position: "fixed",
            top: { xs: 72, sm: 88 },
            left: "50%",
            right: "auto",
            transform: "translateX(-50%)",
            width: "min(420px, calc(100vw - 16px))",
            maxWidth: "calc(100vw - 16px)",
            height: "min(70vh, 520px)",
            maxHeight: "min(70vh, 520px)",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#111",
            color: "#fff",
            border: "1px solid #333",
            borderRadius: 1,
            boxShadow: "0 8px 32px rgba(0,0,0,0.55)",
            zIndex: (theme) => theme.zIndex.modal + 1,
            overflow: "hidden",
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 1.25,
              borderBottom: "1px solid #333",
              flexShrink: 0,
            }}
          >
            <Typography
              id="quick-upload-history-title"
              variant="subtitle1"
              sx={{ fontWeight: 700, color: "hsl(19 101% 51% / 1)" }}
            >
              Quick upload activity
            </Typography>
            <IconButton
              size="small"
              onClick={() => setUploadHistoryOpen(false)}
              aria-label="Close upload activity"
              sx={{ color: "#fff" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box
            sx={{
              px: 2,
              py: 1,
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Typography variant="caption" sx={{ color: "#aaa", mb: 1, display: "block", flexShrink: 0 }}>
              Stored on this device. Drag and drop still works on the document list behind this panel.
            </Typography>
            {uploadHistoryEntries.length === 0 ? (
              <Typography sx={{ color: "#888", py: 2, flexShrink: 0 }}>No uploads recorded yet.</Typography>
            ) : (
              <TableContainer
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  border: "1px solid #2a2a2a",
                  borderRadius: 1,
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ backgroundColor: "#1a1a1a", color: "#fff", fontWeight: 700 }}>
                        File name
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ backgroundColor: "#1a1a1a", color: "#fff", fontWeight: 700, width: 120 }}
                      >
                        Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {uploadHistoryEntries.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell sx={{ color: "#eee", wordBreak: "break-word" }}>{row.fileName}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          {row.status === "success" ? (
                            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, color: "#4caf50" }}>
                              <CheckCircleIcon sx={{ fontSize: 18 }} />
                              success
                            </Box>
                          ) : (
                            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, color: "hsl(19 101% 51% / 1)" }}>
                              <CancelIcon sx={{ fontSize: 18 }} />
                              fail
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              px: 2,
              py: 1.25,
              borderTop: "1px solid #333",
              flexShrink: 0,
            }}
          >
            <Button
              size="small"
              onClick={() => {
                clearQuickUploadHistory();
                setUploadHistoryEntries([]);
              }}
              disabled={uploadHistoryEntries.length === 0}
              sx={{ color: "#ccc", textTransform: "none" }}
            >
              Clear list
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => setUploadHistoryOpen(false)}
              sx={dialogPrimaryButtonSx}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </Paper>
  );
};

export default DocumentList;
